import { agreementsCollection } from '../firebase';


/**
 * RevisionHistoryAdapter (CKEditor 5 v44 compatible)
 *
 * Custom CKEditor Revision History adapter backed by Firebase Firestore.
 * Follows the same closure factory pattern as CommentsAdapter.js and
 * TrackChangesAdapter.js in this project.
 *
 * v44 Adapter interface (from RevisionHistoryAdapter.d.ts):
 *   - getRevision({ revisionId, channelId }) → Promise<RevisionData>
 *   - updateRevisions(data: Array<RevisionData>, channelId: string) → Promise<Array<RevisionData>>
 *
 * Revisions are stored at:
 *   agreements/{agreementId}/revisions/{revisionId}
 *
 * @param {Object} agreementData    - The reactive agreement data object (must have .id)
 * @param {Array}  initialRevisions - Array of revisions pre-loaded from Firestore
 */
export default function getRevisionHistoryAdapter(agreementData, initialRevisions = []) {
    return class RevisionHistoryAdapter {
        constructor(editor) {
            this.editor = editor;
        }

        init() {
            // Guard: if RevisionHistory plugin isn't in the build (stale bundle or
            // license issue), warn and exit cleanly instead of crashing the editor.
            if (!this.editor.plugins.has('RevisionHistory')) {
                console.warn('[RevisionHistoryAdapter] RevisionHistory plugin is not loaded. ' +
                    'Ensure the CKEditor build includes RevisionHistory and the license covers it.');
                return;
            }
            const revisionHistoryPlugin = this.editor.plugins.get('RevisionHistory');

            // ── v44 Adapter interface ─────────────────────────────────────────────
            // Must be assigned BEFORE addRevisionData() — RevisionTracker only wires
            // the per-revision `_update` listener (the thing that flushes changes to
            // adapter.updateRevisions on autosave) when `adapter` is already set at
            // the time the revision is registered. If we pre-load revisions first,
            // they end up listener-less, so RevisionTracker.update() during autosave
            // never reaches Firestore — only explicit saveRevision() does, because
            // that path creates a fresh revision *after* the adapter exists.
            revisionHistoryPlugin.adapter = {
                /**
                 * Returns the full data for a single revision by ID.
                 * Called by CKEditor when the user clicks on a revision in the sidebar.
                 *
                 * @param {{ revisionId: string, channelId: string }} data
                 * @returns {Promise<RevisionData>}
                 */
                getRevision: ({ revisionId, channelId }) => {
                    return agreementsCollection
                        .doc(agreementData.id)
                        .collection('revisions')
                        .doc(revisionId)
                        .get()
                        .then((doc) => {
                            if (!doc.exists) {
                                console.warn(`[RevisionHistoryAdapter] Revision ${revisionId} not found.`);
                                return null;
                            }
                            return RevisionHistoryAdapter._formatRevisionDates(doc.data());
                        })
                        .catch((error) => {
                            console.error('[RevisionHistoryAdapter] Error fetching revision:', error);
                            return null;
                        });
                },

                /**
                 * Called when revision(s) are created or updated.
                 * Receives an array of revision data objects (upsert semantics).
                 *
                 * @param {Array<RevisionData>} revisionsData
                 * @param {string}              channelId
                 * @returns {Promise<Array<RevisionData>>}  Updated revisions with server timestamps
                 */
                updateRevisions: (revisionsData, channelId) => {
                    const batch = agreementsCollection.firestore.batch();

                    revisionsData.forEach((revision) => {
                        const revisionRef = agreementsCollection
                            .doc(agreementData.id)
                            .collection('revisions')
                            .doc(revision.id);

                        // If no createdAt set yet, set it now (server should control this in prod)
                        const dataToSave = {
                            ...revision,
                            createdAt: revision.createdAt || new Date(),
                        };

                        batch.set(revisionRef, dataToSave, { merge: true });
                    });

                    return batch
                        .commit()
                        .then(() => {
                            // Return the revisions with resolved createdAt dates so CKEditor
                            // can display accurate relative timestamps.
                            return revisionsData.map((r) => ({
                                id: r.id,
                                createdAt: r.createdAt || new Date(),
                            }));
                        })
                        .catch((error) => {
                            console.error('[RevisionHistoryAdapter] Error updating revisions:', error);
                            return [];
                        });
                },
            };

            // Pre-load revisions AFTER the adapter is set so that each addRevisionData
            // call wires up its `_update` listener for autosave-driven flushes.
            if (initialRevisions && initialRevisions.length > 0) {
                initialRevisions.forEach((revision) => {
                    const formatted = RevisionHistoryAdapter._formatRevisionDates(revision);
                    try {
                        revisionHistoryPlugin.addRevisionData(formatted);
                    } catch (e) {
                        // Already added or malformed — skip silently
                    }
                });
            }
        }

        /**
         * Converts Firestore Timestamp objects → JS Date objects.
         * Firestore returns Timestamps with a `.seconds` property for timestamps
         * that were stored as Firestore native Timestamps.
         */
        static _formatRevisionDates(revision) {
            if (!revision) return revision;

            const formatted = { ...revision };

            if (
                formatted.createdAt &&
                typeof formatted.createdAt === 'object' &&
                'seconds' in formatted.createdAt
            ) {
                formatted.createdAt = new Date(formatted.createdAt.seconds * 1000);
            }

            return formatted;
        }
    };
}
